import { useState } from "react"

import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"

import { Button } from "@/components/ui/button.tsx"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible.tsx"
import WeatherDropdown from "@/components/WeatherDropdown.tsx"

import ForHireDailyDisplay from "./ForHireDailyDisplay.tsx"
import ForHireGauge from "./ForHireGauge.tsx"
import ForHireLiveMetrics from "./ForHireLiveMetrics.tsx"
import ForHireMonthlyDisplay from "./ForHireMonthlyDisplay.tsx"

import type { ForHireSummaryConfig } from "../../types/forHireSummaryTypes.ts"

export default function ForHireSummaryRow({ config }: { config?: ForHireSummaryConfig }) {
  const [open, setOpen] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1280px)").matches,
  )
  const [viewIndex, setViewIndex] = useState(0)

  if (!config) return <></>

  const views = [
    { key: "gauge", render: () => <ForHireGauge thresholds={config.gaugeThresholds} /> },
    { key: "live", render: () => <ForHireLiveMetrics /> },
    { key: "daily", render: () => <ForHireDailyDisplay /> },
    { key: "monthly", render: () => <ForHireMonthlyDisplay /> },
  ]

  const prevView = () => setViewIndex((i) => (i - 1 + views.length) % views.length)
  const nextView = () => setViewIndex((i) => (i + 1) % views.length)

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="@container/summary flex flex-col gap-2 p-4 bg-muted border rounded-md shadow-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-2xl shrink-0 whitespace-nowrap">
          <span className="hidden @md/summary:inline">SPL For Hire</span>
          <span className="inline @md/summary:hidden">SPL FH</span>
        </h2>
        <div className="flex items-center gap-1 shrink-0">
          {/* SPL For Hire operates out of the BCO plant (location 2), so the
              weather shown here mirrors that location. */}
          <WeatherDropdown locationId={7} />
          {!open && (
            <>
              <Button variant="ghost" size="icon" aria-label="Previous metric" onClick={prevView}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Next metric" onClick={nextView}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={open ? "Hide metrics" : "Show metrics"}>
              <ChevronDown
                className={`h-5 w-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              />
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>

      {!open && <div key={views[viewIndex].key}>{views[viewIndex].render()}</div>}

      <CollapsibleContent className="flex flex-col gap-1.5">
        {views.map((v) => (
          <div key={v.key}>{v.render()}</div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}
